import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { bankAccountService } from "services";
import {
  BankAccountForm,
  BankAccountQuery,
  BankAccountUpdatePayload,
} from "types";
import SnackbarUtils from "utils/SnackbarUtils";

type BankAccountListKey =
  | ["bank-accounts"]
  | ["bank-accounts", BankAccountQuery];
type BankAccountDetailKey = ["bank-accounts", "detail", number | string];

const bankAccountKeys = {
  all: ["bank-accounts"] as const,
  list: (params?: BankAccountQuery): BankAccountListKey =>
    params ? ["bank-accounts", params] : ["bank-accounts"],
  detail: (id: number | string): BankAccountDetailKey => [
    "bank-accounts",
    "detail",
    id,
  ],
};

export function useBankAccount() {
  const queryClient = useQueryClient();

  const fetchList = (params?: BankAccountQuery, enabled = true) => {
    return useQuery({
      queryKey: bankAccountKeys.list(params),
      queryFn: () => bankAccountService.list(params),
      enabled,
      select: (data) => ({
        data: data?.data ?? [],
        totalPage: Math.max(1, data?.totalPage ?? 1),
      }),
    });
  };

  const fetchDetail = (id: number | string, enabled = true) => {
    return useQuery({
      queryKey: bankAccountKeys.detail(id),
      queryFn: () => bankAccountService.show(id),
      enabled: Boolean(id) && enabled,
    });
  };

  const createBankAccount = useMutation({
    mutationFn: (payload: BankAccountForm) => bankAccountService.store(payload),
    onSuccess: (res) => {
      if (res?.statusCode === 200 || res?.statusCode === 201) {
        SnackbarUtils.success(
          res?.message ?? "Tạo tài khoản ngân hàng thành công",
        );
      }
      queryClient.invalidateQueries({ queryKey: bankAccountKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error("Tạo tài khoản ngân hàng thất bại");
      console.error("createBankAccount failed", err);
    },
  });

  const updateBankAccount = useMutation({
    mutationFn: (payload: BankAccountUpdatePayload) =>
      bankAccountService.update(payload),
    onSuccess: (res) => {
      if (res?.statusCode === 200) {
        SnackbarUtils.success(
          res?.message ?? "Cập nhật tài khoản ngân hàng thành công",
        );
      }
      queryClient.invalidateQueries({ queryKey: bankAccountKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error("Cập nhật tài khoản ngân hàng thất bại");
      console.error("updateBankAccount failed", err);
    },
  });

  const deleteBankAccount = useMutation({
    mutationFn: (id: number | string) => bankAccountService.destroy(id),
    onSuccess: (res) => {
      if (res?.statusCode === 200) {
        SnackbarUtils.success(
          res?.message ?? "Xoá tài khoản ngân hàng thành công",
        );
      }
      queryClient.invalidateQueries({ queryKey: bankAccountKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error("Xoá tài khoản ngân hàng thất bại");
      console.error("deleteBankAccount failed", err);
    },
  });

  return {
    fetchList,
    fetchDetail,
    createBankAccount,
    updateBankAccount,
    deleteBankAccount,
  };
}
